import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Chip, Divider, InputAdornment, ListItemButton, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { deleteGroupIdUserAPI, getAuthoritiesAPI, postGroupIdUserAPI } from 'apis/settings/settingsAPI';
import { useTranslation } from 'react-i18next';
import { FixedSizeList as List, ListChildComponentProps } from 'react-window';
import { USER_TYPE } from '../../constants/constants';
import SearchIcon from '@mui/icons-material/Search';

type UserType = 'EMPLOYEE' | 'NATIVE' | 'FOREIGNER';

function GroupManagement() {
  const [checkedIncludedUser, setCheckedIncludedUser] = useState<number[][]>([]);
  const [checkedExcludedUser, setCheckedExcludedUser] = useState<number[][]>([]);

  const [users, setUsers] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<any[]>([]);
  const [tabIndex, setTabIndex] = useState(0); // 탭 인덱스 상태 추가
  const [filterText, setFilterText] = useState('');
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation(); // useTranslation 훅 사용

  const [listHeight, setListHeight] = useState(window.innerHeight > 658 ? window.innerHeight - 458 : 200); // 초기 높이 설정

  useEffect(() => {
    const updateHeight = () => {
      if (window.innerWidth <= 768) {
        setListHeight(400); // 모바일일 때 높이 설정
      } else {
        setListHeight(window.innerHeight > 658 ? window.innerHeight - 458 : 200); // 기본 높이 설정
      }
    };

    updateHeight(); // 초기 렌더링 시 실행
    window.addEventListener('resize', updateHeight); // 리사이즈 이벤트 등록

    return () => {
      window.removeEventListener('resize', updateHeight); // 클린업
    };
  }, []);

  // API에서 데이터 가져와 상태 업데이트
  useEffect(() => {
    const fetchAuthorities = async () => {
      try {
        const data = await getAuthoritiesAPI(); // API 응답 대기
        setUsers(data.data.users); // 사용자 상태 설정
        setUserGroups(data.data.userGroups); // 사용자 그룹 상태 설정
      } catch (error) {
        // console.error('권한관리 API 호출 중 오류 발생:', error); // 에러 처리
        console.error(error);
      }
    };

    fetchAuthorities(); // 함수 호출
  }, []);

  const handleIncludeToggle = (groupIndex: number, userId: number) => {
    setCheckedIncludedUser((prevState) => {
      const newState = [...prevState];
      if (!newState[groupIndex]) {
        newState[groupIndex] = [];
      }
      const userIndex = newState[groupIndex].indexOf(userId);
      if (userIndex !== -1) {
        newState[groupIndex].splice(userIndex, 1);
      } else {
        newState[groupIndex].push(userId);
      }
      return newState;
    });
  };

  const handleExcludeToggle = (groupIndex: number, userId: number) => {
    setCheckedExcludedUser((prevState) => {
      const newState = [...prevState];
      if (!newState[groupIndex]) {
        newState[groupIndex] = [];
      }
      const userIndex = newState[groupIndex].indexOf(userId);
      if (userIndex !== -1) {
        newState[groupIndex].splice(userIndex, 1);
      } else {
        newState[groupIndex].push(userId);
      }
      return newState;
    });
  };

  // 그룹 사용자 삭제
  const handleCheckedRight = async (groupIndex: number) => {
    const userIds = checkedIncludedUser[groupIndex];
    // console.log('그룹 사용자 삭제', userIds, userGroups[groupIndex]);

    try {
      // 사용자 삭제 API 호출
      await deleteGroupIdUserAPI(userGroups[groupIndex].id, { userIds });

      // 데이터 업데이트를 위해 getAuthoritiesAPI 다시 호출
      const updatedData = await getAuthoritiesAPI();
      setCheckedIncludedUser((prevState) => {
        const newState = [...prevState];
        newState[groupIndex] = [];
        return newState;
      });
      setUsers(updatedData.data.users);
      setUserGroups(updatedData.data.userGroups);
    } catch (error) {
      // console.error('사용자 삭제 중 오류 발생:', error);
      console.error(error);
    }
  };

  // 그룹 사용자 추가
  const handleCheckedLeft = async (groupIndex: number) => {
    const userIds = checkedExcludedUser[groupIndex];

    try {
      // 사용자 추가 API 호출
      await postGroupIdUserAPI(userGroups[groupIndex].id, { userIds });

      // 데이터 업데이트를 위해 getAuthoritiesAPI 다시 호출
      const updatedData = await getAuthoritiesAPI();
      setCheckedExcludedUser((prevState) => {
        const newState = [...prevState];
        newState[groupIndex] = [];
        return newState;
      });
      setUsers(updatedData.data.users);
      setUserGroups(updatedData.data.userGroups);
    } catch (error) {
      // console.error('사용자 추가 중 오류 발생:', error);
      console.error(error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
  };

  const Row = ({ index, style, data }: ListChildComponentProps<{ users: any[]; handleToggle: (userId: number) => void; checkedUsers: number[] }>) => {
    const { users, handleToggle, checkedUsers } = data;
    const user = users[index];

    return (
      <ListItemButton
        key={user.id}
        role="listitem"
        dense
        onClick={() => handleToggle(user.id)}
        style={style}
        sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box>
          <Checkbox checked={checkedUsers.includes(user.id)} tabIndex={-1} size="small" disableRipple />
          {`${user.username} (${user.name})`}
        </Box>
        <Chip size="small" label={t(USER_TYPE[user.type as UserType]?.name) || ''} sx={{ fontSize: '12px', height: 20 }} />
      </ListItemButton>
    );
  };

  return (
    <Box padding={2} display="flex" flexDirection="column" gap={2} sx={{ width: { xs: '100vw', sm: '100%' } }}>
      <Box
        // padding={2}
        // paddingBottom={2}
        borderRadius={1}
        // bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('group_management.title')}
        </Typography>

        <Divider flexItem />
      </Box>

      <Tabs value={tabIndex} onChange={handleTabChange} aria-label="그룹 관리 탭">
        {userGroups?.map((userGroup, index) => <Tab key={userGroup.id} label={userGroup.name} />)}
      </Tabs>

      {userGroups?.map((userGroup, groupIndex) => {
        const filteredIncludedUsers = users.filter(
          (user) => userGroup.userIds.includes(user.id) && (user.username.includes(filterText) || user.name.includes(filterText))
        );
        const filteredExcludedUsers = users.filter(
          (user) => !userGroup.userIds.includes(user.id) && (user.username.includes(filterText) || user.name.includes(filterText))
        );

        return (
          tabIndex === groupIndex && (
            <Box
              key={userGroup.id}
              padding={2}
              paddingBottom={2}
              borderRadius={1}
              bgcolor={theme.palette.contentBlock.background}
              display="flex"
              flexDirection="column"
              gap={2}
              sx={{ width: '100%' }}
            >
              <Box display="flex" flexDirection="row" alignItems="center" gap={1} justifyContent="space-between">
                <Box display="flex" flexDirection="row" alignItems="end" gap={1}>
                  <Typography variant="h5" fontWeight="bold">
                    {userGroup.name}
                  </Typography>
                  <Typography fontSize="16px" fontWeight="bold" color={theme.palette.mode === 'dark' ? '#cfcfcf' : '#5f5f5f'}>
                    {userGroup.note}
                  </Typography>
                </Box>

                <TextField
                  value={filterText}
                  size="small"
                  onChange={handleFilterChange}
                  placeholder={t('group_management.search_placeholder')}
                  sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'none' : '#ffffff', height: 29, width: 150 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ marginLeft: -1, marginRight: '-12px' }} />
                        </InputAdornment>
                      )
                    }
                  }}
                />
              </Box>

              <Box
                display="flex"
                flexDirection="row"
                justifyContent="center"
                alignItems="center"
                gap={2}
                sx={{
                  width: 'auto',
                  flexDirection: { xs: 'column', sm: 'row' }, // 모바일에서 세로 정렬
                  gap: 2
                }}
              >
                <Box
                  width={100}
                  flexGrow={1}
                  borderRadius={1}
                  boxShadow={1}
                  display="flex"
                  flexDirection="column"
                  gap={0}
                  sx={{
                    minWidth: '300px',
                    width: '100%'
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    padding={1}
                    paddingX={2}
                    bgcolor={theme.palette.table.header}
                    sx={{
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px'
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('group_management.in_group_user')}
                    </Typography>

                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      {checkedIncludedUser[groupIndex] && checkedIncludedUser[groupIndex].length > 0 && (
                        <Typography color="primary" sx={{ userSelect: 'none' }}>
                          ({checkedIncludedUser[groupIndex].length})
                        </Typography>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCheckedRight(groupIndex)}
                        disabled={!checkedIncludedUser[groupIndex] || checkedIncludedUser[groupIndex].length === 0}
                        sx={{ borderRadius: '4px' }}
                      >
                        {t('group_management.out_group')}
                      </Button>
                    </Box>
                  </Box>

                  <Divider />

                  <List
                    height={listHeight}
                    itemCount={filteredIncludedUsers.length}
                    itemSize={32}
                    width="100%"
                    itemData={{
                      users: filteredIncludedUsers,
                      handleToggle: handleIncludeToggle.bind(null, groupIndex),
                      checkedUsers: checkedIncludedUser[groupIndex] || []
                    }}
                    style={{
                      backgroundColor: theme.palette.table.row
                    }}
                  >
                    {Row}
                  </List>
                </Box>

                <Box
                  width={100}
                  flexGrow={1}
                  borderRadius={1}
                  boxShadow={1}
                  display="flex"
                  flexDirection="column"
                  gap={0}
                  sx={{
                    minWidth: '300px',
                    width: '100%'
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    padding={1}
                    paddingX={2}
                    bgcolor={theme.palette.table.header}
                    sx={{
                      borderTopLeftRadius: '8px',
                      borderTopRightRadius: '8px'
                    }}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleCheckedLeft(groupIndex)}
                        disabled={!checkedExcludedUser[groupIndex] || checkedExcludedUser[groupIndex].length === 0}
                        sx={{ borderRadius: '4px' }}
                      >
                        {t('group_management.in_group')}
                      </Button>
                      {checkedExcludedUser[groupIndex] && checkedExcludedUser[groupIndex].length > 0 && (
                        <Typography color="primary" sx={{ userSelect: 'none' }}>
                          ({checkedExcludedUser[groupIndex].length})
                        </Typography>
                      )}
                    </Box>

                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('group_management.out_group_user')}
                    </Typography>
                  </Box>

                  <Divider />

                  <List
                    height={listHeight}
                    itemCount={filteredExcludedUsers.length}
                    itemSize={32}
                    width="100%"
                    itemData={{
                      users: filteredExcludedUsers,
                      handleToggle: handleExcludeToggle.bind(null, groupIndex),
                      checkedUsers: checkedExcludedUser[groupIndex] || []
                    }}
                    style={{
                      backgroundColor: theme.palette.table.row
                    }}
                  >
                    {Row}
                  </List>
                </Box>
              </Box>
            </Box>
          )
        );
      })}
    </Box>
  );
}

export default GroupManagement;
